/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Notify from "@eisinc/App/Components/Notify";
import PDFLinkCell from "@eisinc/App/Components/PaymentCenter/PDFLinkCell";
import getInvoicePdf from "@eisinc/App/Handlers/GetInvoicePdf";
import getSystemList from "@eisinc/App/Handlers/XNG/getSystemList";
import { GetSystemListValuesModel } from "@eisinc/App/Services/SystemListService";
import {
    formatter,
    getPaginatedInvoicePayments,
    handleSortInvoicePayments,
    handleSwitchSortArrow,
} from "@eisinc/App/Utilities/PaymentCenterUtilities";
import Pagination, { PaginationPresentationProps } from "@eisinc/MobiusOverrides/Pagination";
import { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import {
    DEFAULT_DUE_DATE_RANGE_KEY,
    DEFAULT_INVOICE_PAYMENT_SORT_KEY,
    DEFAULT_PAGE_SIZE,
    InvoicePaymentAttributes,
    SORT_INVOICE_PAYMENT_BY_FIELD,
} from "@eisinc/XDL/constants";
import { InvoicePaymentModel } from "@eisinc/XDL/interfaces";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

const mapDispatchToProps = {
    getInvoicePdf,
    getSystemList,
};

export interface OpenItemsTableStyles {
    noResultsContainer?: InjectableCss;
    loadingSpinnerWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    dateRangeSelect?: SelectPresentationProps;
    invoicesSummaryContainer?: InjectableCss;
    invoiceSummaryTitle?: TypographyPresentationProps;
    invoiceSummaryTitleCredit?: TypographyPresentationProps;
    invoiceSummaryTitleNonCredit?: TypographyPresentationProps;
    tableWrapper?: InjectableCss;
    dataCellHeader?: DataTableHeaderProps;
    dataRow?: DataTableRowProps;
    dataCell?: DataTableCellProps;
    invoiceAmountDataCell?: DataTableCellProps;
    originalInvoiceNumberDataCell?: DataTableCellProps;
    paginationWrapper?: InjectableCss;
    pagination?: PaginationPresentationProps;
}

export const openItemsTableStyles: OpenItemsTableStyles = {};

interface OpenItemsTableProps {
    invoicePaymentItems?: InvoicePaymentModel[];
    isLoadingInvoices?: boolean;
}

type Props = OpenItemsTableProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const OpenItemsTable: React.FunctionComponent<Props> = ({
    invoicePaymentItems,
    language,
    isLoadingInvoices,
    getSystemList,
}) => {
    const styles = openItemsTableStyles;
    const toasterContext = React.useContext(ToasterContext);

    const [invoicePayments, setInvoicePayments] = React.useState<InvoicePaymentModel[] | undefined>();
    const [sortOrder, setSortOrder] = React.useState(SORT_INVOICE_PAYMENT_BY_FIELD);
    const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
    const [page, setPage] = React.useState(1);
    const pageSizeOptions = [DEFAULT_PAGE_SIZE];
    const [dueDateRanges, setDueDateRanges] = React.useState<GetSystemListValuesModel[]>([]);
    const [selectedDateRange, setSelectedDateRange] = React.useState<string>(DEFAULT_DUE_DATE_RANGE_KEY);
    const [selectedSortKey, setSelectedSortKey] = React.useState<keyof InvoicePaymentModel>(
        DEFAULT_INVOICE_PAYMENT_SORT_KEY,
    );
    const [isProcessingData, setIsProcessingData] = React.useState(true);

    const handleSortInvoicePaymentsEvent = (sortField: keyof InvoicePaymentModel) => {
        setSelectedSortKey(sortField);
        const updateSortOrder = handleSwitchSortArrow(sortField, sortOrder);
        setSortOrder(updateSortOrder);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        if (!newPageSize) {
            setPage(1);
            setPageSize(1000);
        } else {
            setPageSize(newPageSize);
        }
    };

    const loadDueDateRangesDropDown = () => {
        getSystemList({
            systemListName: "Custom_InvoicePaymentDueDateRangeDropDown",
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    const sortedData = resultProps.apiResult.result.sort(
                        (a, b) => parseInt(a.additionalInfo, 10) - parseInt(b.additionalInfo, 10),
                    );

                    setSelectedDateRange(sortedData[0].name);
                    setDueDateRanges(sortedData);
                }
            },
        });
    };

    const filteredInvoicePayments = React.useMemo(() => {
        return invoicePayments?.filter(invoicePayment => {
            const [min, max] = selectedDateRange
                .slice(1, -1)
                .split(", ")
                .map(item => (item === "Infinity" ? Infinity : parseFloat(item)));
            const { dueDatePast } = invoicePayment;
            if (max === Infinity) {
                return dueDatePast >= min;
            }
            return dueDatePast >= min && dueDatePast <= max;
        });
    }, [selectedDateRange, invoicePayments]);

    const sortedInvoicesPayments = React.useMemo(() => {
        return handleSortInvoicePayments(filteredInvoicePayments, selectedSortKey, sortOrder);
    }, [sortOrder, selectedSortKey, filteredInvoicePayments]);

    const processedInvoicePayments = React.useMemo(() => {
        setIsProcessingData(false);
        return getPaginatedInvoicePayments(page, pageSize, sortedInvoicesPayments);
    }, [page, pageSize, sortedInvoicesPayments, sortOrder]);

    const invoicePaymentStatistics = React.useMemo(() => {
        let creditInvoicesNumber = 0;
        let paidInvoicesNumber = 0;
        let creditInvoicesSum = 0;
        let paidInvoicesSum = 0;

        filteredInvoicePayments?.forEach(invoice => {
            if (invoice.invoiceAmount < 0) {
                creditInvoicesNumber++;
                creditInvoicesSum += invoice.invoiceAmount;
            } else if (invoice.invoiceAmount > 0) {
                paidInvoicesNumber++;
                paidInvoicesSum += invoice.invoiceAmount;
            } else {
                paidInvoicesNumber++;
            }
        });

        creditInvoicesSum = parseFloat(Math.abs(creditInvoicesSum).toFixed(2));
        paidInvoicesSum = parseFloat(paidInvoicesSum.toFixed(2));
        return {
            creditInvoicesNumber,
            creditInvoicesSum,
            paidInvoicesNumber,
            paidInvoicesSum,
        };
    }, [filteredInvoicePayments]);

    const rows = processedInvoicePayments?.map(invoice => {
        return {
            id: invoice.invoiceItemNumber,
            invoiceNumber: invoice.invoiceNumber,
            po: invoice.purchaseOrderNumber,
            invoiceDate: invoice.invoiceDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.invoiceDate),
                      language,
                  })
                : "",
            sapFiDocumentType: invoice.sapFiDocumentType,
            itemText: invoice.itemText,
            calculatedDueDate: invoice.calculatedDueDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.calculatedDueDate),
                      language,
                  })
                : "",
            invoiceAmount: invoice.invoiceAmount,
            invoiceAmountDisplay: invoice.invoiceAmountDisplay,
            originalInvoiceNumber: invoice.originalInvoiceNumber,
        } as any;
    });

    React.useLayoutEffect(() => {
        if (isLoadingInvoices) {
            setIsProcessingData(isLoadingInvoices);
        }
    }, [isLoadingInvoices]);

    React.useEffect(() => {
        if (invoicePaymentItems) {
            setInvoicePayments(invoicePaymentItems);
        }
    }, [invoicePaymentItems]);

    React.useEffect(() => {
        loadDueDateRangesDropDown();
    }, []);

    if (isProcessingData || rows === undefined) {
        return (
            <StyledWrapper {...styles.loadingSpinnerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!isProcessingData && rows?.length === 0) {
        return (
            <>
                <StyledWrapper {...styles.headerWrapper}>
                    <StyledWrapper>
                        <Select
                            {...styles.dateRangeSelect}
                            name="dueDateRange"
                            label={translate("Due Date Range")}
                            value={selectedDateRange || ""}
                            disabled={false}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                setSelectedDateRange(e.currentTarget.value)
                            }
                            data-test-selector="invoicePayment_DateRange"
                        >
                            {dueDateRanges?.map(dateRange => (
                                <option key={dateRange.name} value={dateRange.name}>
                                    {dateRange.description}
                                </option>
                            ))}
                        </Select>
                    </StyledWrapper>

                    <StyledWrapper {...styles.invoicesSummaryContainer}>
                        <Typography {...styles.invoiceSummaryTitle}>
                            {invoicePaymentStatistics.paidInvoicesNumber} Invoices:{" "}
                            <Typography {...styles.invoiceSummaryTitleNonCredit}>
                                {formatter.format(invoicePaymentStatistics.paidInvoicesSum)}
                            </Typography>
                        </Typography>
                        |
                        <Typography {...styles.invoiceSummaryTitle}>
                            {invoicePaymentStatistics.creditInvoicesNumber} Credits:{" "}
                            <Typography {...styles.invoiceSummaryTitleCredit}>
                                {formatter.format(invoicePaymentStatistics.creditInvoicesSum)}
                            </Typography>
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.noResultsContainer}>
                    <Notify
                        messageType="danger"
                        notifyContainerOverrides={css`
                            max-width: 100%;
                        `}
                        body={siteMessage("Custom_Payment_Center_No_Invoices_Found")}
                    />
                </StyledWrapper>
            </>
        );
    }

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.headerWrapper}>
                <StyledWrapper>
                    <Select
                        {...styles.dateRangeSelect}
                        name="dueDateRange"
                        label={translate("Due Date Range")}
                        value={selectedDateRange || ""}
                        disabled={false}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            setSelectedDateRange(e.currentTarget.value)
                        }
                        data-test-selector="invoicePayment_DateRange"
                    >
                        {dueDateRanges?.map(dateRange => (
                            <option key={dateRange.name} value={dateRange.name}>
                                {dateRange.description}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>

                <StyledWrapper {...styles.invoicesSummaryContainer}>
                    <Typography {...styles.invoiceSummaryTitle}>
                        {invoicePaymentStatistics.paidInvoicesNumber} Invoices:{" "}
                        <Typography {...styles.invoiceSummaryTitleNonCredit}>
                            {formatter.format(invoicePaymentStatistics.paidInvoicesSum)}
                        </Typography>
                    </Typography>
                    |
                    <Typography {...styles.invoiceSummaryTitle}>
                        {invoicePaymentStatistics.creditInvoicesNumber} Credits:{" "}
                        <Typography {...styles.invoiceSummaryTitleCredit}>
                            {invoicePaymentStatistics.creditInvoicesSum > 0 ? "-" : ""}
                            {formatter.format(invoicePaymentStatistics.creditInvoicesSum)}
                        </Typography>
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
            <>
                <StyledWrapper {...styles.tableWrapper}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader
                                sorted={sortOrder.originalInvoiceNumber}
                                onSortClick={() =>
                                    handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.originalInvoiceNumber)
                                }
                                data-test-selector="tableHeader_originalInvoiceNumber"
                                {...styles.dataCellHeader}
                            >
                                {translate("IPInvoice")}
                            </DataTableHeader>
                            <DataTableHeader
                                sorted={sortOrder.invoiceItemNumber}
                                onSortClick={() =>
                                    handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceItemNumber)
                                }
                                data-test-selector="tableHeader_invoiceItemNumber"
                                {...styles.dataCellHeader}
                            >
                                {translate("IPDOC")}
                            </DataTableHeader>
                            <DataTableHeader
                                sorted={sortOrder.purchaseOrderNumber}
                                onSortClick={() =>
                                    handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.purchaseOrderNumber)
                                }
                                data-test-selector="tableHeader_purchaseOrderNumber"
                                {...styles.dataCellHeader}
                            >
                                {translate("PO #")}
                            </DataTableHeader>
                            <DataTableHeader
                                sorted={sortOrder.invoiceDate}
                                onSortClick={() => handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceDate)}
                                data-test-selector="tableHeader_invoiceDate"
                                {...styles.dataCellHeader}
                            >
                                {translate("Doc. Date")}
                            </DataTableHeader>
                            <DataTableHeader
                                sorted={sortOrder.sapFiDocumentType}
                                onSortClick={() =>
                                    handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.sapFiDocumentType)
                                }
                                data-test-selector="tableHeader_sapFiDocumentType"
                                {...styles.dataCellHeader}
                            >
                                {translate("Doc. Type")}
                            </DataTableHeader>
                            <DataTableHeader
                                sorted={sortOrder.itemText}
                                onSortClick={() => handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.itemText)}
                                data-test-selector="tableHeader_itemText"
                                {...styles.dataCellHeader}
                            >
                                {translate("Notes")}
                            </DataTableHeader>
                            <DataTableHeader
                                sorted={sortOrder.calculatedDueDate}
                                onSortClick={() =>
                                    handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.calculatedDueDate)
                                }
                                data-test-selector="tableHeader_calculatedDueDate"
                                {...styles.dataCellHeader}
                            >
                                {translate("Due Date")}
                            </DataTableHeader>
                            <DataTableHeader
                                sorted={sortOrder.invoiceAmount}
                                onSortClick={() =>
                                    handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceAmount)
                                }
                                data-test-selector="tableHeader_invoiceAmount"
                                {...styles.dataCellHeader}
                            >
                                {translate("Amount")}
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody data-test-selector="invoicePaymentTable_tableBody">
                            {rows?.map(
                                (
                                    {
                                        id,
                                        po,
                                        invoiceDate,
                                        sapFiDocumentType,
                                        itemText,
                                        calculatedDueDate,
                                        invoiceAmountDisplay,
                                        invoiceNumber,
                                    },
                                    rowIndex,
                                ) => (
                                    <DataTableRow key={id} {...styles.dataRow}>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_originalInvoiceNumber"
                                            {...styles.originalInvoiceNumberDataCell}
                                        >
                                            <PDFLinkCell invoice={rows[rowIndex]} showOriginalInvoiceNumber />
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_invoiceNumber">
                                            {invoiceNumber}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_purchaseNumber"
                                            {...styles.dataCell}
                                        >
                                            {po}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_invoiceDate"
                                            {...styles.dataCell}
                                        >
                                            {invoiceDate}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_sapFiDocumentType"
                                            {...styles.dataCell}
                                        >
                                            {sapFiDocumentType}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_itemText"
                                            {...styles.dataCell}
                                        >
                                            {itemText}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_calculatedDueDate"
                                            {...styles.dataCell}
                                        >
                                            {calculatedDueDate}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_invoiceAmount"
                                            {...styles.invoiceAmountDataCell}
                                        >
                                            {invoiceAmountDisplay}
                                        </DataTableCell>
                                    </DataTableRow>
                                ),
                            )}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>

                <StyledWrapper {...styles.paginationWrapper}>
                    <Pagination
                        resultsCount={filteredInvoicePayments?.length || 0}
                        currentPage={page}
                        resultsPerPage={pageSize}
                        resultsPerPageLabel={translate("Per Page")}
                        resultsPerPageOptions={pageSizeOptions}
                        onChangeResultsPerPage={changeResultsPerPage}
                        onChangePage={(newPageIndex: number) => setPage(newPageIndex || 1)}
                        withAllOption
                        {...styles.pagination}
                    />
                </StyledWrapper>
            </>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OpenItemsTable);
